/**
 * useSSE — Real-time event stream from VPS Trading Engine
 *
 * Connects to the VPS SSE endpoint via Cloudflare Tunnel.
 * Receives: market_data, order_update, broker_status, system events
 * Auto-reconnects on disconnect with exponential backoff.
 */
import { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react'
// NotificationContext no longer used here — system events go to header banner
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'

const VPS_SSE_URL = 'https://tunnel.la-money.co.in/events'

export type SSEEvent = {
  type: 'market_data' | 'order_update' | 'broker_status' | 'system' | 'connected'
  data: any
  timestamp: number
}

export interface SSEState {
  connected: boolean
  lastEvent: SSEEvent | null
  marketData: Record<string, any>  // token → latest tick
  orderUpdates: any[]              // last 50 order events
  brokerStatuses: Record<string, any>  // brokerId → status
  eventCount: number
  systemError: string | null
  systemLogs: any[]                  // running history of system logs
}

const SSEContext = createContext<SSEState | null>(null)

function useSSESocket(enabled = true) {
  const [state, setState] = useState<SSEState>({
    connected: false,
    lastEvent: null,
    marketData: {},
    orderUpdates: [],
    brokerStatuses: {},
    eventCount: 0,
    systemError: null,
    systemLogs: [],
  })

  // System events go to header banner, not notification bell
  const { addToast } = useToast()
  const eventSourceRef = useRef<EventSource | null>(null)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef<any>(null)

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    try {
      const es = new EventSource(VPS_SSE_URL)
      eventSourceRef.current = es

      es.onopen = () => {
        console.log('[SSE] Connected to trading engine')
        retryCountRef.current = 0
        const logItem = {
          id: `log-conn-${Date.now()}`,
          type: 'success',
          message: 'Connected to VPS Trading Engine event stream',
          ts: Date.now()
        }
        setState(prev => ({
          ...prev,
          connected: true,
          systemLogs: [logItem, ...prev.systemLogs].slice(0, 500)
        }))
      }

      // Initial connection event
      es.addEventListener('connected', (e) => {
        const data = JSON.parse(e.data)
        console.log('[SSE] Initial state:', data)
        const brokerMap: Record<string, any> = {}
        if (data.brokers) {
          data.brokers.forEach((b: any) => { brokerMap[b.id] = b })
        }
        setState(prev => ({
          ...prev,
          connected: true,
          brokerStatuses: brokerMap,
          lastEvent: { type: 'connected', data, timestamp: Date.now() },
        }))
      })

      // Market data ticks
      es.addEventListener('market_data', (e) => {
        const data = JSON.parse(e.data)
        setState(prev => ({
          ...prev,
          marketData: { ...prev.marketData, [data.indexCode || data.token || data.gtoken || 'unknown']: data },
          eventCount: prev.eventCount + 1,
          lastEvent: { type: 'market_data', data, timestamp: Date.now() },
        }))
      })

      // Order updates
      es.addEventListener('order_update', (e) => {
        const data = JSON.parse(e.data)
        setState(prev => ({
          ...prev,
          orderUpdates: [data, ...prev.orderUpdates].slice(0, 50),
          eventCount: prev.eventCount + 1,
          lastEvent: { type: 'order_update', data, timestamp: Date.now() },
        }))

        // Notify on order events
        if (data.type === 'order_placed') {
          addToast(`Order placed: ${data.order?.symbol || 'unknown'}`, 'success', 5000)
        }
      })

      // Broker status changes
      es.addEventListener('broker_status', (e) => {
        const data = JSON.parse(e.data)
        setState(prev => ({
          ...prev,
          brokerStatuses: { ...prev.brokerStatuses, [data.id]: data },
          eventCount: prev.eventCount + 1,
          lastEvent: { type: 'broker_status', data, timestamp: Date.now() },
        }))
      })

      // System events (errors, warnings, info)
      es.addEventListener('system', (e) => {
        const data = JSON.parse(e.data)
        const logItem = {
          id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: data.type || 'info',
          message: data.message || '',
          details: data.details || {},
          ts: data.ts || Date.now()
        }

        setState(prev => {
          let systemError = prev.systemError
          if (data.type === 'error') {
            systemError = data.message
          } else if (data.type === 'warning') {
            systemError = `⚡ ${data.message}`
          } else if (data.type === 'success') {
            systemError = null
          }

          return {
            ...prev,
            systemLogs: [logItem, ...prev.systemLogs].slice(0, 500),
            systemError,
            eventCount: prev.eventCount + 1,
            lastEvent: { type: 'system', data, timestamp: Date.now() },
          }
        })

        // Trigger notification bell if error or warning
        try {
          // We will wire this in NotificationContext to listen to lastEvent
        } catch (_) {}

        if (data.type === 'error') {
          if ((window as any)._sseErrTimer) clearTimeout((window as any)._sseErrTimer)
          ;(window as any)._sseErrTimer = setTimeout(() => {
            setState(prev => ({ ...prev, systemError: null }))
          }, 8000)
        } else if (data.type === 'warning') {
          if ((window as any)._sseErrTimer) clearTimeout((window as any)._sseErrTimer)
          ;(window as any)._sseErrTimer = setTimeout(() => {
            setState(prev => ({ ...prev, systemError: null }))
          }, 6000)
        }
      })

      es.onerror = () => {
        console.warn('[SSE] Connection error — will retry')
        const logItem = {
          id: `log-err-${Date.now()}`,
          type: 'error',
          message: 'Connection to VPS Trading Engine SSE stream failed/disconnected',
          ts: Date.now()
        }
        setState(prev => ({
          ...prev,
          connected: false,
          systemLogs: [logItem, ...prev.systemLogs].slice(0, 500)
        }))
        es.close()
        eventSourceRef.current = null

        // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
        const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000)
        retryCountRef.current++
        console.log(`[SSE] Retrying in ${delay / 1000}s...`)
        retryTimerRef.current = setTimeout(connect, delay)
      }

    } catch (err) {
      console.error('[SSE] Failed to create EventSource:', err)
    }
  }, [addToast])

  useEffect(() => {
    if (!enabled) return

    connect()

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
      }
    }
  }, [enabled, connect])

  return state
}

export function SSEProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth()
  const state = useSSESocket(!!token)

  return (
    <SSEContext.Provider value={state}>
      {children}
    </SSEContext.Provider>
  )
}

export function useSSE() {
  const ctx = useContext(SSEContext)
  if (!ctx) throw new Error('useSSE must be used within an SSEProvider')
  return ctx
}
